import SitePhoto from '@/components/Photo'
import { localizeHref } from '@/i18n/href'
import { useI18n } from '@/i18n/I18nProvider'
import { Ctas, type SectionOf } from './shared'

/*
 * The band is centred, not ragged-left. Asked for on 2026-08-30 looking at the
 * DAIMI band: "explore daimi ve explore aimi ortalı olsun … bu tasarım daha gri
 * kutuya hakim ve ortalı olsun". The wheel was already centred in the grey box
 * while the heading, the intro, the caption and the button all started at the
 * left edge, so the drawing read as belonging to a different section than the
 * words above it. Everything now shares one centred column, and the wheel is a
 * step wider so it fills the band rather than floating in it.
 */
export default function ScaleSection({ section }: { section: SectionOf<'scale'> }) {
  const { locale } = useI18n()
  return (
    <div className="text-center">
      {section.heading && (
        <h2 className="mx-auto max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">
          {section.heading}
        </h2>
      )}
      {section.sub && (
        <p className="mx-auto mt-4 max-w-3xl text-lg text-muted-foreground">{section.sub}</p>
      )}
      {section.intro && (
        <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed">{section.intro}</p>
      )}
      {/* Centred, so the accent rule that used to sit down its left edge would
          now be a line pinned to one side of a centred block. The larger,
          heavier setting is what marks it as the quote. */}
      {section.pullquote && (
        <p className="mx-auto mt-10 max-w-3xl text-xl font-medium leading-relaxed">
          {section.pullquote}
        </p>
      )}
      {/* A numbered ladder still reads left-to-right; only the column is centred. */}
      {section.levels && (
        <ol className="mx-auto mt-12 max-w-3xl text-left">
          {section.levels.map((l) => (
            <li key={l.n} className="flex gap-6 border-t border-border py-5">
              <span className="font-mono text-sm text-muted-foreground">{l.n}</span>
              <div>
                <p className="font-bold">{l.name}</p>
                <p className="mt-1 text-muted-foreground">{l.body}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
      {/* The wheel, in the band rather than under it. Never cropped — the
          dimension names are printed down both edges of these exports, so
          it keeps its own ratio and scales to the column whole, the same
          treatment `figure` gives it. */}
      {section.photo && (
        <figure className="mt-10">
          {section.cta ? (
            <a href={localizeHref(section.cta.href, locale)} className="block" aria-label={section.cta.label}>
              <SitePhoto
                photo={section.photo}
                sizes="(max-width: 1152px) 92vw, 1152px"
                className="mx-auto h-auto w-full max-w-6xl"
              />
            </a>
          ) : (
            <SitePhoto
              photo={section.photo}
              sizes="(max-width: 1152px) 92vw, 1152px"
              className="mx-auto h-auto w-full max-w-6xl"
            />
          )}
          {section.caption && (
            <figcaption className="mx-auto mt-4 max-w-3xl text-sm text-muted-foreground">
              {section.caption}
            </figcaption>
          )}
        </figure>
      )}
      {/* `Ctas` lays its buttons out in a full-width flex row, so centring the
          text is not enough — the row itself has to be shrunk to its contents
          and centred. */}
      {section.cta && (
        <div className="flex justify-center">
          <Ctas items={[section.cta]} />
        </div>
      )}
    </div>
  )
}
